import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, ChevronDown, AlertCircle } from 'lucide-react';
import type { ChemicalWithRegistration } from '../../types/spray';
import sprayService from '../../services/spray';
import { filterByPests, pestCoverage } from '@/utils/targetPests';

interface ChemicalSearchProps {
  situationId?: number | null;
  /** Pest keys to narrow a situation's registrations to. Empty = no filter. */
  targetPests?: string[];
  value?: ChemicalWithRegistration | null;
  onChange: (chemical: ChemicalWithRegistration) => void;
  error?: string;
}

function ChemicalSearch({
  situationId,
  targetPests,
  value,
  onChange,
  error,
}: ChemicalSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ChemicalWithRegistration[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selected = value ?? null;
  // A stable dependency for the search callback — a fresh array every render
  // would re-run the search on every keystroke of the parent.
  const pestFilterKey = (targetPests ?? []).join('|');
  const pestFilter = pestFilterKey ? pestFilterKey.split('|') : [];
  const coverage = pestFilter.length > 1 ? pestCoverage(results, pestFilter) : null;

  const search = useCallback(async (q: string) => {
    setLoading(true);
    try {
      if (situationId) {
        const res = await sprayService.getChemicalsBySituation(situationId);
        if (res.success && res.data) {
          const byPest = filterByPests(res.data as ChemicalWithRegistration[], pestFilterKey ? pestFilterKey.split('|') : []);
          const filtered = byPest.filter(c =>
            !q || c.product_name.toLowerCase().includes(q.toLowerCase()) ||
            (c.trade_name || '').toLowerCase().includes(q.toLowerCase()) ||
            (c.target_pest || '').toLowerCase().includes(q.toLowerCase())
          );
          setResults(filtered);
        }
      } else {
        const res = await sprayService.searchChemicals(q);
        if (res.success && res.data) {
          setResults(res.data as ChemicalWithRegistration[]);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [situationId, pestFilterKey]);

  useEffect(() => {
    if (open) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => search(query), 300);
    }
    return () => clearTimeout(debounceRef.current);
  }, [query, open, search]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (chem: ChemicalWithRegistration) => {
    setOpen(false);
    setQuery('');
    onChange(chem);
  };

  const signalBadge = (word?: string | null) => {
    if (!word) return null;
    const colors: Record<string, string> = {
      DANGER: 'bg-red-100 text-red-700',
      WARNING: 'bg-amber-100 text-amber-700',
      CAUTION: 'bg-yellow-100 text-yellow-700',
    };
    return (
      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${colors[word] || 'bg-neutral-100 text-neutral-600'}`}>
        {word}
      </span>
    );
  };

  // Where a registration came from, when it is not the situation itself: a
  // broader group's registration is valid here but worth naming, and a caveat
  // ("see label for exceptions") must travel with every row it applies to.
  const registrationSource = (chem: ChemicalWithRegistration) => (
    <>
      {chem.matched_relation === 'broader' && chem.matched_situation_name && (
        <p className="text-xs text-neutral-500 break-words">
          Registered for {chem.matched_situation_name}
        </p>
      )}
      {chem.situation_caveat && (
        <p className="text-xs text-amber-700 break-words flex items-start gap-1">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-px" /> {chem.situation_caveat}
        </p>
      )}
    </>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {selected ? (
        <div
          className={`border rounded-lg p-3 bg-white cursor-pointer hover:border-primary-400 transition-colors ${
            error ? 'border-red-300' : 'border-neutral-300'
          }`}
          onClick={() => setOpen(!open)}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-neutral-900 text-sm">{selected.product_name}</span>
                {selected.trade_name && (
                  <span className="text-neutral-500 text-xs">({selected.trade_name})</span>
                )}
                {signalBadge(selected.signal_word)}
              </div>
              {selected.target_pest && (
                <p className="text-xs text-neutral-500 mt-0.5">Target: {selected.target_pest}</p>
              )}
              {registrationSource(selected)}
              <div className="flex flex-wrap gap-3 mt-1 text-xs text-neutral-500">
                {(selected.min_rate_per_ha || selected.max_rate_per_ha) && (
                  <span>
                    Rate: {selected.min_rate_per_ha ?? '?'}–{selected.max_rate_per_ha ?? '?'} {selected.rate_unit}
                  </span>
                )}
                {selected.whp_days != null && <span>WHP Harvest: {selected.whp_days}d</span>}
                {selected.whp_export_days != null && <span>WHP Grazing: {selected.whp_export_days}d</span>}
                {selected.rei_hours != null && <span>REI: {selected.rei_hours}h</span>}
              </div>
            </div>
            <ChevronDown className="h-4 w-4 text-neutral-400 flex-shrink-0 mt-1" />
          </div>
        </div>
      ) : (
        <div
          className={`flex items-center gap-2 border rounded-lg px-3 py-2.5 bg-white cursor-text ${
            error ? 'border-red-300' : 'border-neutral-300'
          }`}
          onClick={() => { setOpen(true); }}
        >
          <Search className="h-4 w-4 text-neutral-400 flex-shrink-0" />
          <input
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={situationId
              ? pestFilter.length ? 'Search chemicals registered for these pests…' : 'Search chemicals registered for this situation…'
              : 'Search chemicals…'}
            className="flex-1 text-sm outline-none bg-transparent placeholder-neutral-400"
          />
        </div>
      )}

      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
          <AlertCircle className="h-3.5 w-3.5" /> {error}
        </p>
      )}

      {open && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg">
          {selected && (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-100">
              <Search className="h-4 w-4 text-neutral-400" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search to change selection…"
                className="flex-1 text-sm outline-none placeholder-neutral-400"
                autoFocus
              />
            </div>
          )}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-3 text-sm text-neutral-500 text-center">Searching…</div>
            ) : results.length === 0 ? (
              <div className="p-3 text-sm text-neutral-500 text-center">
                {situationId
                  ? pestFilter.length
                    ? 'No chemicals registered for the selected pests on this situation'
                    : 'No registered chemicals found for this situation'
                  : 'No chemicals found'}
              </div>
            ) : (
              results.map(chem => (
                <button
                  key={`${chem.id}-${chem.registration_id}`}
                  type="button"
                  onClick={() => handleSelect(chem)}
                  className={`w-full text-left px-4 py-3 hover:bg-primary-50 transition-colors border-b border-neutral-50 last:border-b-0 ${
                    selected?.id === chem.id ? 'bg-primary-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="font-medium text-sm text-neutral-900">{chem.product_name}</span>
                    {chem.trade_name && <span className="text-neutral-400 text-xs">({chem.trade_name})</span>}
                    {signalBadge(chem.signal_word)}
                    {coverage && (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                        {coverage.get(chem.id)?.size ?? 0} of {pestFilter.length} pests
                      </span>
                    )}
                  </div>
                  {chem.target_pest && (
                    <p className="text-xs text-neutral-500">Target: {chem.target_pest}</p>
                  )}
                  {registrationSource(chem)}
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-neutral-400">
                    {(chem.min_rate_per_ha || chem.max_rate_per_ha) && (
                      <span>{chem.min_rate_per_ha ?? '?'}–{chem.max_rate_per_ha ?? '?'} {chem.rate_unit}</span>
                    )}
                    {chem.whp_days != null && <span>WHP {chem.whp_days}d</span>}
                    {chem.rei_hours != null && <span>REI {chem.rei_hours}h</span>}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ChemicalSearch;
